/**
 * jsonb-api.ts — Phase 438 Plan 01 (VAL-01) pure jsonb-aware validator API.
 *
 * Purpose:
 *   The forward-target entry point Phase 437 ETL outputs, Phase 439 RAG/MCP,
 *   and Phase 440 cold-start cache consume to validate Layer 2 mutations
 *   *without* round-tripping through the filesystem. The existing
 *   `validate(path)` / `validateBlock(blockDir)` / `validateApp(appDir)`
 *   API stays UNTOUCHED for backward compat (see ./validator.ts) — Plan 01
 *   is purely additive.
 *
 * Contract (locked by 438-CONTEXT.md D-A2 — see PLAN frontmatter `truths`):
 *
 *   ```ts
 *   export interface ValidateActionFlowInput {
 *     vocabulary: TripleJson[];     // L1 vocabulary fixtures or snapshot rows
 *     instance: TripleJson[];        // L2 application_ontology rows for one block
 *     options?: PureValidatorOptions;
 *   }
 *
 *   export interface PureValidatorOptions {
 *     stages?: string[];             // subset of "structural" | "actionflow" | "runtime-expr"
 *     failOnWarning?: boolean;
 *   }
 *
 *   export async function validateActionFlow(
 *     input: ValidateActionFlowInput,
 *   ): Promise<ValidationReport>;
 *   ```
 *
 * Implementation:
 *   1. Reconstruct an in-memory BlockBundle from `input.instance` via
 *      `triplesToBlockBundle` (jsonb-api/triples-to-bundle.ts — Plan 01
 *      minimum-viable: prefers `cogo:bundleAs` short-circuit, falls back to
 *      empty bundle).
 *   2. Run the requested stage runners (default: actionflow + runtime-expr).
 *      All three Plan-01-supported stages are already fs-free at the source
 *      (see stages/stage-actionflow.ts, stages/stage-runtime-expr.ts).
 *   3. Aggregate `StageResult[]` into a `ValidationReport` via the inlined
 *      `buildReport` helper. The original `buildReport` lives in
 *      ./validator.ts (which imports node:fs); we duplicate the small (~20
 *      LOC) report-shape function here so jsonb-api.ts has zero fs-tainted
 *      transitive imports. The duplication is acceptable per
 *      CLAUDE.md "Bug Fix & Parity Rules" §3 (no magic numbers in two
 *      places — `buildReport` is shape-only logic, no constants).
 *
 * NOTES:
 *   - `vocabulary` parameter is currently UNUSED by Plan 01's stage list.
 *     Future stages that need ontology vocabulary input (SHACL, contract,
 *     semantic) will consume it; the parameter is part of the locked D-A2
 *     contract and reserved for that. Plan 01's three stages do not need
 *     vocabulary input — runActionFlowStage / runRuntimeExprStage operate
 *     purely on the BlockBundle's flow/ui/events trees.
 *   - `runStructuralStage` from stages/stage-structural.ts imports `node:fs`
 *     transitively (Plan 02 splits it into pure-inner / IO-outer); its pure
 *     form will be wired into this API by Plan 02.
 *
 * VAL-04 compliance: zero `node:fs` / `node:path` / `node:url` /
 * `node:module` imports — verified at file head and by Plan 02's ESLint
 * `no-restricted-imports` rule.
 */

import type {
  BlockReport,
  FixSummary,
  StageResult,
  ValidationReport,
} from "./types"
import { computeGrade, computeScore } from "./scoring"
import { runActionFlowStage } from "./stages/stage-actionflow"
import { runRuntimeExprStage } from "./stages/stage-runtime-expr"
import type { TripleJson } from "./jsonb-api/codec"
import { triplesToBlockBundle } from "./jsonb-api/triples-to-bundle"

/**
 * Public input shape for `validateActionFlow` (D-A2 verbatim — DO NOT
 * diverge; this is the contract Phase 437/439/440 build against).
 */
export interface ValidateActionFlowInput {
  /** L1 vocabulary fixtures or `vocabulary_snapshot` MV rows. */
  vocabulary: TripleJson[]
  /** L2 `application_ontology` rows for one block (or app). */
  instance: TripleJson[]
  /** Optional subset of `ValidatorOptions` — no path-shaped fields. */
  options?: PureValidatorOptions
}

/**
 * Pure-API options subset of `ValidatorOptions`. Excludes path-shaped
 * fields (ontologyDir, rulesPath, llmApiUrl, e2eBaseUrl) and CLI-orchestrator
 * flags (fix, withLlm, e2e) — those live on the file-IO `validate(path)`
 * surface in ./validator.ts. Plan 02 may move that surface to ./io/.
 */
export interface PureValidatorOptions {
  /** Restrict execution to the named stages. Default: ["actionflow", "runtime-expr"]. */
  stages?: string[]
  /** When true, treat any warning-level issue as a graded failure. */
  failOnWarning?: boolean
}

/**
 * Default stage list for `validateActionFlow`. Both stages are pure
 * (zero fs imports per audit at Plan 01 entry).
 */
const DEFAULT_STAGES: ReadonlyArray<string> = ["actionflow", "runtime-expr"]

/**
 * Validate an in-memory ActionFlow instance against an in-memory vocabulary
 * graph. Pure: no file IO, no network IO, deterministic given input.
 *
 * @param input.vocabulary  L1 vocabulary triples (currently unused — reserved).
 * @param input.instance    L2 application instance triples (one block).
 * @param input.options     Stage selection + grading thresholds.
 * @returns                 Structurally-valid `ValidationReport`.
 */
export async function validateActionFlow(
  input: ValidateActionFlowInput,
): Promise<ValidationReport> {
  const start = Date.now()
  const requested = input.options?.stages ?? DEFAULT_STAGES

  // Reconstruct synthetic BlockBundle from the instance triples.
  const bundle = triplesToBlockBundle(input.instance)

  // Run the requested pure stage runners. Stages not yet wired (e.g.
  // "structural" — Plan 02 will surface its pure form) are silently
  // skipped here; Plan 02's ESLint + Plan 03's parity tests will catch
  // any drift if stages get wired without test coverage.
  const stages: StageResult[] = []

  if (requested.includes("actionflow")) {
    stages.push(runActionFlowStage(bundle))
  }
  if (requested.includes("runtime-expr")) {
    stages.push(runRuntimeExprStage(bundle))
  }

  return buildPureReport(
    "inline:jsonb",
    "block",
    stages,
    start,
    undefined,
    undefined,
  )
}

/**
 * Inline copy of `buildReport` from ./validator.ts so this module has zero
 * fs-tainted transitive imports (validator.ts imports node:fs for path
 * existence checks; the report-shape function does not depend on any fs
 * call). The two implementations MUST stay in sync; if validator.ts's
 * buildReport changes shape, mirror it here.
 *
 * Source: ./validator.ts L191-231 (Phase 438 baseline 2026-05-10).
 */
function buildPureReport(
  target: string,
  targetType: "block" | "app",
  stages: StageResult[],
  startTime: number,
  blocks?: Record<string, BlockReport>,
  fixSummary?: FixSummary,
): ValidationReport {
  const allIssues = stages.flatMap((s) => s.issues)

  let score: number
  if (blocks && Object.keys(blocks).length > 0) {
    const blockScores = Object.values(blocks).map((b) => b.score)
    score = Math.round(
      blockScores.reduce((a, b) => a + b, 0) / blockScores.length,
    )
  } else {
    score = computeScore(allIssues)
  }
  const grade = computeGrade(score)

  return {
    version: "1.0.0",
    timestamp: new Date().toISOString(),
    target,
    targetType,
    duration: Date.now() - startTime,
    summary: {
      score,
      grade,
      totalIssues: allIssues.length,
      errors: allIssues.filter((i) => i.level === "error").length,
      warnings: allIssues.filter((i) => i.level === "warning").length,
      infos: allIssues.filter((i) => i.level === "info").length,
    },
    stages,
    blocks,
    fixSummary,
  }
}

/** Re-export `TripleJson` so callers consume one package. */
export type { TripleJson } from "./jsonb-api/codec"
